import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, vModelText as _vModelText, withModifiers as _withModifiers, withKeys as _withKeys, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "hc-header__search" }
const _hoisted_2 = ["aria-activedescendant", "aria-expanded", "placeholder", "onKeydown"]
const _hoisted_3 = {
  key: 0,
  class: "autocomplete__list",
  id: "autocomplete-default__listbox",
  role: "listbox"
}
const _hoisted_4 = ["tabindex", "aria-selected", "aria-posinset", "aria-setsize", "id", "onClick", "onMouseenter"]
const _hoisted_5 = ["data-text", "innerHTML"]

import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { Loader } from "@sazka/design-system/src/loader/loader";
// eslint-disable-next-line vue/no-dupe-keys
import { debounce } from "@sazka/utils/debounce";
import { UrlUtils } from "@sazka/utils/url";
import { SearchAnalytics } from "@/shared/utils/analytics/analytics-search";
import { useHcSearch } from "../composables/use-hc-search";
import { ArticlePreviewDto } from "../model/article";


export default /*@__PURE__*/_defineComponent({
  __name: '_hc-search',
  props: {
    minInputLength: { default: 2 },
    debounce: { default: 500 },
    placeholder: {},
    enableResultPage: { type: Boolean, default: true }
  },
  setup(__props: any) {

const props = __props;

const { fetchResults, hideSearchResults, fetchAutocomplete } = useHcSearch();

const inputRef = ref<HTMLInputElement>(null);
const items = ref<ArticlePreviewDto[]>([]);
const isInputFocused = ref(false);
const searchText = ref("");
const currentSelectionIndex = ref(null);
const isListVisible = computed(
    () => isInputFocused.value && searchText.value.length >= props.minInputLength && items.value.length
);
const currentSelection = computed(() =>
    isListVisible.value && currentSelectionIndex.value < items.value.length
        ? items.value[currentSelectionIndex.value]
        : null
);

const escapeRegExp = (string: string) => string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const highlightTextMatch = (text: string) => {
    const regexp = new RegExp(`(${escapeRegExp(searchText.value)})`, "ig");
    return text.replace(regexp, "<strong>$1</strong>");
};

const transformationHtml = (item: ArticlePreviewDto) =>
    `<a href="${item.pageUrl.relative}">${highlightTextMatch(item.name)}</a>`;
const transformationText = (item: ArticlePreviewDto) => item.name;

const fetch = async () => {
    if (searchText.value && searchText.value.length >= props.minInputLength) {
        items.value = await fetchAutocomplete(searchText.value);
    } else {
        items.value = [];
    }
    SearchAnalytics.pushSearchSuggester("help center", searchText.value, items.value.length);
};

const selectItem = (item: ArticlePreviewDto) => {
    SearchAnalytics.pushSuggesterClick("centrum podpory", currentSelection.value.name);
    searchText.value = transformationText(item);
    window.location.href = currentSelection.value.pageUrl.relative;
    Loader.show();
};

const onInput = () => {
    currentSelectionIndex.value = null;
};

const onFocus = () => {
    isInputFocused.value = true;
};

const onBlur = () => {
    isInputFocused.value = false;
};

const onArrowDown = () => {
    if (!isListVisible.value) return;
    if (currentSelectionIndex.value === null) currentSelectionIndex.value = 0;
    else if (currentSelectionIndex.value < items.value.length - 1) currentSelectionIndex.value++;
};

const onArrowUp = () => {
    if (!isListVisible.value) return;
    if (currentSelectionIndex.value === null) currentSelectionIndex.value = items.value.length - 1;
    else if (currentSelectionIndex.value > 0) currentSelectionIndex.value--;
    else currentSelectionIndex.value = null;
};

const onEscapeDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
        isInputFocused.value = false;
    }
};

const selectCurrentSelection = async () => {
    if (currentSelection.value) {
        selectItem(currentSelection.value);
        hideSearchResults();
    } else if (props.enableResultPage) {
        try {
            Loader.show();
            await fetchResults(searchText.value);
            inputRef.value.blur();
        } finally {
            Loader.hide();
        }
    } else {
        Loader.show();
        window.location.href = `/centrum-podpory?search-text=${searchText.value}`;
    }
};

const loadLinksDebounced = debounce(() => fetch(), props.debounce);
watch(searchText, loadLinksDebounced);

onMounted(() => {
    document.addEventListener("keydown", (e) => onEscapeDown(e));
    if (props.enableResultPage) {
        const query = UrlUtils.getQueryString("search-text");
        if (query) {
            searchText.value = query;
            selectCurrentSelection();
        }
    }
});

onUnmounted(() => {
    document.removeEventListener("keydown", (e) => onEscapeDown(e));
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("label", _hoisted_1, [
    _createElementVNode("img", {
      class: "ico-a-centrum-glass hc-header__search-icon",
      alt: "",
      src: "/frontend/web/img/help-center/centrum-glass.svg",
      onClick: _cache[0] || (_cache[0] = ($event: any) => (selectCurrentSelection()))
    }),
    _withDirectives(_createElementVNode("input", {
      type: "text",
      role: "combobox",
      "aria-activedescendant": 
                isListVisible.value && currentSelectionIndex.value ? `autocomplete-default__option--${currentSelectionIndex.value}` : null
            ,
      "aria-expanded": isListVisible.value ? true : false,
      "aria-owns": "autocomplete-default__listbox",
      "aria-autocomplete": "list",
      autocomplete: "off",
      class: "hc-header__search-input form-control",
      ref_key: "inputRef",
      ref: inputRef,
      placeholder: _ctx.placeholder,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((searchText).value = $event)),
      onInput: onInput,
      onFocus: onFocus,
      onBlur: onBlur,
      onKeydown: [
        _withKeys(_withModifiers(onArrowDown, ["prevent"]), ["down"]),
        _withKeys(_withModifiers(onArrowUp, ["prevent"]), ["up"]),
        _withKeys(_withModifiers(selectCurrentSelection, ["prevent"]), ["enter"])
      ]
    }, null, 40, _hoisted_2), [
      [_vModelText, searchText.value]
    ]),
    (isListVisible.value)
      ? (_openBlock(), _createElementBlock("ul", _hoisted_3, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(items.value, (item, index) => {
            return (_openBlock(), _createElementBlock("li", {
              role: "option",
              tabindex: currentSelectionIndex.value === index ? null : -1,
              "aria-selected": currentSelectionIndex.value === index ? true : false,
              "aria-posinset": index,
              "aria-setsize": items.value.length,
              id: `autocomplete-default__option--${index}`,
              class: _normalizeClass(["autocomplete__list-item", { 'autocomplete__list-item--active': currentSelectionIndex.value === index }]),
              key: index,
              onMousedown: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["prevent"])),
              onClick: ($event: any) => (selectItem(item)),
              onMouseenter: ($event: any) => (currentSelectionIndex.value = index)
            }, [
              _createElementVNode("span", {
                class: "autocomplete__list-item-text",
                "data-text": transformationText(item),
                innerHTML: transformationHtml(item)
              }, null, 8, _hoisted_5)
            ], 42, _hoisted_4))
          }), 128))
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})